import React, { useEffect, useState, useMemo, memo } from "react";
import {
  ProductCardProductsType,
  ProductCardCompareLabelWeightsType,
  ProductCardIsinToNameType,
} from "types/AssistantTypes";
import styles from "./anagraficaCompare.module.scss";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components/ui/tooltip";
import { QuestionMarkIcon } from "shared/assets/svg/QuestionMarkIcon";

type AnagraficaCompareProps = {
  data: ProductCardCompareLabelWeightsType[];
  allProducts: ProductCardProductsType;
  isinToName: ProductCardIsinToNameType;
  productTypes: Record<string, string>;
};

const AnagraficaCompareComponent: React.FC<AnagraficaCompareProps> = ({
  data,
  allProducts,
  isinToName,
  productTypes,
}) => {
  const { t } = useTranslation();

  const [columns, setColumns] = useState(3);
  const rows = Array.from({ length: 17 }, (_, rowIndex) => rowIndex + 1);

  useEffect(() => {
    if (allProducts) {
      const productCount = Object.keys(allProducts).length;
      setColumns(productCount + 1);
    }
  }, [allProducts]);

  // Check if all products are bonds
  const areBonds = useMemo(() => {
    if (!productTypes || Object.keys(productTypes).length === 0) return false;
    return Object.values(productTypes).every(type => type === "Obbligazioni" || type === "BOND");
  }, [productTypes]);

  const cells = useMemo(
    () =>
      rows.flatMap((row) =>
        Array.from({ length: columns }, (_, colIndex) => ({
          row,
          col: colIndex + 1,
        }))
      ),
    [rows, columns]
  );

  const renderedCells = useMemo(() => {
    if (!areBonds) return null;

    return cells.map((cell) => {
      const { row, col } = cell;

      // First column contains labels
      if (col === 1) {
        const labelData = data[row - 1];
        if (!labelData) return null;

        return (
          <div key={`${row}-${col}`} className={styles.cellKeys}>
            {labelData.label}
          </div>
        );
      }

      // Other columns contain values
      const productIndex = col - 2;
      const productIsins = Object.keys(allProducts);
      const currentIsin = productIsins[productIndex];
      
      if (!currentIsin) return null;

      const rowData = data[row - 1];
      if (!rowData) return null;

      const value = rowData.weights[productIndex];
      
      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          {value === "-" ? "-" : value}
        </div>
      );
    });
  }, [cells, allProducts, isinToName, data, areBonds]);

  if (!areBonds) return null;

  return (
    <div className={styles.anagraficaContainer}>
      <h3 className={styles.title}>{t("productCard.anagrafica")}</h3>
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
          {renderedCells}
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent re-renders if props haven't changed
export const AnagraficaCompare = memo(
  AnagraficaCompareComponent,
  (prevProps, nextProps) => {
    // Only re-render if data or productTypes changes
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      JSON.stringify(prevProps.productTypes) === JSON.stringify(nextProps.productTypes)
    );
  }
);

export default AnagraficaCompare;
